// ============================================================
// PusherService — リアルタイム通知接続
//
// Pusher チャンネルを購読し、受信したイベントで
// reservationSyncService / vehicleSyncService のキャッシュを即時更新。
// ============================================================

import Pusher from 'pusher-js';
import { mapReservation, mapVehicle } from '../api/dispatchMappers';
import { reservationSyncService } from './reservationSyncService';
import { vehicleSyncService } from './vehicleSyncService';
import { saveChangeToHistory } from './changeHistory';
import { getDeviceId, getDeviceName } from './deviceIdentifier';
import { FIELD_LABELS } from '../constants/fieldLabels';
import type { ChangeRecord, FieldChange } from '../types';

type PusherEventCallback = (eventName: string, data: any) => void;

interface PusherState {
  connected: boolean;
  socketId: string | null;
  state: string;
}

class PusherService {
  pusher: any;
  channel: any;
  lineChannel: any;
  listeners: PusherEventCallback[];
  lineListeners: PusherEventCallback[];
  connected: boolean;

  constructor() {
    this.pusher = null;
    this.channel = null;
    this.lineChannel = null;
    this.listeners = [];
    this.lineListeners = [];
    this.connected = false;
  }

  connect(): void {
    if (this.pusher) return;

    const key = process.env.REACT_APP_PUSHER_APP_KEY;
    const cluster = process.env.REACT_APP_PUSHER_CLUSTER;

    if (!key || !cluster) {
      console.warn('[PusherService] REACT_APP_PUSHER_APP_KEY or REACT_APP_PUSHER_CLUSTER not set');
      return;
    }

    // デバッグ用: Pusher のログを有効化（本番では無効にする）
    (Pusher as any).logToConsole = process.env.NODE_ENV === 'development';

    this.pusher = new Pusher(key, {
      cluster,
      forceTLS: true,
    });

    // 接続状態の監視
    this.pusher.connection.bind('connected', () => {
      this.connected = true;
      console.log('[PusherService] Connected to Pusher');
    });

    this.pusher.connection.bind('disconnected', () => {
      this.connected = false;
      console.log('[PusherService] Disconnected from Pusher');
    });

    this.pusher.connection.bind('error', (err: any) => {
      console.error('[PusherService] Connection error:', err);
    });

    // チャンネル購読
    this.channel = this.pusher.subscribe('dispatch-updates');

    this.channel.bind('pusher:subscription_succeeded', () => {
      console.log('[PusherService] Subscribed to channel: dispatch-updates');
    });

    this.channel.bind('pusher:subscription_error', (err: any) => {
      console.error('[PusherService] Subscription error:', err);
    });

    // 全イベントをキャッチ → リスナーに通知
    this.channel.bind_global((eventName: string, data: any) => {
      if (eventName.startsWith('pusher:')) return;
      console.log(`[PusherService] Event: ${eventName}`, data);
      this.notifyListeners(eventName, data);
    });

    // ===== LINE Chat チャンネル =====
    this.lineChannel = this.pusher.subscribe('line-chat');

    this.lineChannel.bind('pusher:subscription_succeeded', () => {
      console.log('[PusherService] Subscribed to channel: line-chat');
    });

    this.lineChannel.bind_global((eventName: string, data: any) => {
      if (eventName.startsWith('pusher:')) return;
      console.log(`[PusherService] LINE Event: ${eventName}`, data);
      this.notifyLineListeners(eventName, data);
    });

    // ===== 予約イベント =====
    this.channel.bind('reservation.created', (data: any) => {
      console.log('[PusherService] reservation.created:', data);
      this.handleReservationUpdate(data);
      this.sendBrowserNotification(
        '新しい予約',
        data?.customer_name
          ? `${data.customer_name} の予約が作成されました`
          : '新しい予約が作成されました'
      );
    });

    this.channel.bind('reservation.updated', (data: any) => {
      console.log('[PusherService] reservation.updated:', data);
      this.handleReservationUpdate(data);
      this.sendBrowserNotification(
        '予約更新',
        data?.customer_name
          ? `${data.customer_name} の予約が更新されました`
          : '予約が更新されました'
      );
    });

    // ===== 車両イベント =====
    this.channel.bind('vehicle.updated', (data: any) => {
      console.log('[PusherService] vehicle.updated:', data);
      this.handleVehicleUpdate(data);
    });

    this.channel.bind('vehicle.swapped', (data: any) => {
      console.log('[PusherService] vehicle.swapped:', data);
      if (data?.vehicles && Array.isArray(data.vehicles)) {
        for (const v of data.vehicles) {
          this.handleVehicleUpdate(v);
        }
      }
    });

    // ===== マスターデータイベント =====
    this.channel.bind('vehicle-type.updated', (data: any) => {
      console.log('[PusherService] vehicle-type.updated:', data);
      // 車種マスタ更新 → 車両データを再取得して反映
      // Pusher でキャッシュ即時更新済みのため forceSync 不要
    });

    this.channel.bind('status.updated', (data: any) => {
      console.log('[PusherService] status.updated:', data);
      // ステータスマスタ更新 → 全データ再取得
      // Pusher でキャッシュ即時更新済みのため forceSync 不要
      // Pusher でキャッシュ即時更新済みのため forceSync 不要
    });

    this.channel.bind('vehicle-check.updated', (data: any) => {
      console.log('[PusherService] vehicle-check.updated:', data);
      // Pusher でキャッシュ即時更新済みのため forceSync 不要
    });

    // ===== スタッフ優先度イベント =====
    this.channel.bind('staff-priority.updated', (data: any) => {
      console.log('[PusherService] staff-priority.updated:', data);
      // 自分自身が発火したイベントは無視（localStorage は既に更新済み）
      try {
        const myDeviceId = getDeviceId();
        if (data?.device_id && data.device_id === myDeviceId) return;
      } catch { /* ignore */ }
      if (data?.priorities && typeof data.priorities === 'object') {
        try {
          localStorage.setItem('staff_priorities', JSON.stringify(data.priorities));
          // 既存リスナー（onStaffPriorityChange 経由）を起動
          window.dispatchEvent(new CustomEvent('staff-priority-updated', { detail: data.priorities }));
        } catch (e) {
          console.error('[PusherService] Failed to apply staff-priority.updated:', e);
        }
      }
    });

    // ===== 支払いイベント =====
    this.channel.bind('payment.updated', (data: any) => {
      console.log('[PusherService] payment.updated:', data);
      // ブラウザ通知
      this.sendBrowserNotification(
        '支払い更新',
        data?.contact_code
          ? `予約 ${data.contact_code} の支払いが更新されました`
          : '支払いが更新されました'
      );
      // 変更履歴に記録（PusherToast用）
      const record: ChangeRecord = {
        id: `${Date.now()}_payment_${data?.contact_code || 'unknown'}`,
        timestamp: new Date().toISOString(),
        entityType: 'payment',
        entityId: data?.contact_code || '',
        changeType: 'update',
        changes: [],
        source: 'pusher',
        summary: `${data?.contact_code || ''} の支払いが更新されました（${data?.invoice_status || ''}）`,
      };
      saveChangeToHistory(record);
      window.dispatchEvent(new CustomEvent('pusher-change-recorded', { detail: record }));
    });

    console.log('[PusherService] Connecting...', { key: key.slice(0, 6) + '...', cluster });
  }

  // ===== キャッシュ即時更新 + 変更履歴記録 =====

  /**
   * 2つのオブジェクト間の差分フィールドを取得
   */
  computeDiff(oldObj: any, newObj: any, skipFields: Set<string> = new Set()): FieldChange[] {
    const changes: FieldChange[] = [];
    if (!oldObj || !newObj) return changes;
    const allKeys = new Set([...Object.keys(oldObj), ...Object.keys(newObj)]);

    for (const key of allKeys) {
      if (key.startsWith('_') || skipFields.has(key)) continue;

      const oldVal = oldObj[key];
      const newVal = newObj[key];

      // 空値同士はスキップ
      const isOldEmpty = oldVal === null || oldVal === undefined || oldVal === '';
      const isNewEmpty = newVal === null || newVal === undefined || newVal === '';
      if (isOldEmpty && isNewEmpty) continue;

      // オブジェクト・配列はJSON比較
      if (JSON.stringify(oldVal) !== JSON.stringify(newVal)) {
        changes.push({
          field: key,
          label: (FIELD_LABELS as any)[key] || key,
          oldValue: oldVal,
          newValue: newVal,
        });
      }
    }
    return changes;
  }

  /** 予約変更用のスキップフィールド */
  static RESERVATION_SKIP_FIELDS = new Set([
    '_', 'updated_at', 'updatedAt', 'created_at', 'createdAt',
    'resourceId', 'resource_id', 'vehicleId',
    'status_name', 'status_code',
    'communication_tools_display',
    'booking_date', 'bookingDate',
    'past_reservation_count',
    'resource_id_r',
    'vehicle', 'detail', // ネストオブジェクト（detail内は別途比較）
    'bg_color', 'text_color',
    'start', 'end', // start_date/end_date と重複
    'reservation_id', // id と重複
    'json_data',
  ]);

  /** 車両変更用のスキップフィールド */
  static VEHICLE_SKIP_FIELDS = new Set([
    '_', 'updated_at', 'updatedAt', 'created_at', 'createdAt',
    'status_name', 'status_code',
    'last_checked_at',
    'check_situations',
    'vehicle_type', // ネストオブジェクト
    'resourceId', 'resource_id', 'vehicleId', 'vehicle_id',
    'bg_color', 'text_color', 'color1', 'color',
    'car_type', 'car_type_id', 'car_type_code',
    'vehicle_type_id', 'vehicle_type_page_id', 'vehicle_type_page_ids',
  ]);

  /**
   * 変更履歴レコードを保存し、UIにリフレッシュイベントを発火
   */
  saveAndNotifyHistory(record: ChangeRecord): void {
    saveChangeToHistory(record);
    // ChangeManagerContext にリフレッシュを通知するカスタムイベント
    window.dispatchEvent(new CustomEvent('pusher-change-recorded', { detail: record }));
    console.log('[PusherService] Change history recorded:', (record as any).summary);
  }

  /** 予約データをキャッシュに即時反映 + 変更履歴記録 */
  handleReservationUpdate(data: any): void {
    if (!data || !data.id) return;
    try {
      const mapped = mapReservation(data);
      if (!mapped) return;

      const id = String(mapped.id);
      const existing = reservationSyncService.cachedReservations?.get(id);

      // updated_at 比較: 既存データより古ければ無視
      if (existing?.updated_at && mapped.updated_at) {
        if (new Date(mapped.updated_at as string) <= new Date(existing.updated_at as string)) {
          console.log(`[PusherService] Skipping older reservation update: ${id}`);
          return;
        }
      }

      // 変更履歴を記録（キャッシュ更新前に差分を取る）
      const changeType = existing ? 'update' : 'create';
      const changes = existing ? this.computeDiff(existing, mapped, PusherService.RESERVATION_SKIP_FIELDS) : [];
      const customerName = mapped.customer_name || '不明';
      const fieldCount = changes.length;

      let summary: string;
      if (changeType === 'create') {
        summary = `予約 #${mapped.id}（${customerName}）が追加されました`;
      } else if (fieldCount > 0) {
        const fieldNames = changes.slice(0, 2).map(f => f.label).join('、');
        const more = fieldCount > 2 ? ` 他${fieldCount - 2}件` : '';
        summary = `予約 #${mapped.id}（${customerName}）の${fieldNames}${more}が更新されました`;
      } else {
        summary = `予約 #${mapped.id}（${customerName}）が更新されました`;
      }

      const record: ChangeRecord = {
        id: `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        timestamp: new Date().toISOString(),
        entityType: 'reservation',
        entityId: mapped.id,
        changeType,
        changeTypeLabel: changeType === 'create' ? '新規作成' : '更新',
        source: 'pusher',
        deviceId: getDeviceId(),
        deviceName: getDeviceName(),
        changes,
        summary,
        metadata: { pusherEvent: changeType === 'create' ? 'reservation.created' : 'reservation.updated' },
      };

      this.saveAndNotifyHistory(record);

      // キャッシュ更新
      reservationSyncService.cachedReservations?.set(id, mapped);
      const all = reservationSyncService.getReservations?.() || [];
      reservationSyncService.listeners?.forEach((cb: any) => {
        try { cb([{ type: 'pusher_update', id }], all); } catch (e) { /* ignore */ }
      });
      console.log(`[PusherService] Reservation cache updated: ${id} (${mapped.customer_name})`);
    } catch (e) {
      console.error('[PusherService] handleReservationUpdate error:', e);
    }
  }

  /** 車両データをキャッシュに即時反映 + 変更履歴記録 */
  handleVehicleUpdate(data: any): void {
    if (!data || !data.id) return;
    try {
      const mapped = mapVehicle(data);
      if (!mapped) return;

      const id = String(mapped.id);
      const existing = vehicleSyncService.cachedVehicles?.get(id);

      // updated_at 比較
      if (existing?.updated_at && mapped.updated_at) {
        if (new Date(mapped.updated_at as string) <= new Date(existing.updated_at as string)) {
          console.log(`[PusherService] Skipping older vehicle update: ${id}`);
          return;
        }
      }

      // 変更履歴を記録（キャッシュ更新前に差分を取る）
      const changeType = existing ? 'update' : 'create';
      const changes = existing ? this.computeDiff(existing, mapped, PusherService.VEHICLE_SKIP_FIELDS) : [];
      const vehicleName = mapped.vehicle_code || '不明';
      const fieldCount = changes.length;

      let summary: string;
      if (changeType === 'create') {
        summary = `車両「${vehicleName}」が追加されました`;
      } else if (fieldCount > 0) {
        const fieldNames = changes.slice(0, 2).map(f => f.label).join('、');
        const more = fieldCount > 2 ? ` 他${fieldCount - 2}件` : '';
        summary = `車両「${vehicleName}」の${fieldNames}${more}が更新されました`;
      } else {
        summary = `車両「${vehicleName}」が更新されました`;
      }

      const record: ChangeRecord = {
        id: `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        timestamp: new Date().toISOString(),
        entityType: 'vehicle',
        entityId: mapped.id,
        changeType,
        changeTypeLabel: changeType === 'create' ? '新規作成' : '更新',
        source: 'pusher',
        deviceId: getDeviceId(),
        deviceName: getDeviceName(),
        changes,
        summary,
        metadata: { pusherEvent: 'vehicle.updated' },
      };

      this.saveAndNotifyHistory(record);

      // キャッシュ更新
      vehicleSyncService.cachedVehicles?.set(id, mapped);
      const all = vehicleSyncService.getVehicles?.() || {};
      vehicleSyncService.listeners?.forEach((cb: any) => {
        try { cb([{ type: 'pusher_update', id }], all); } catch (e) { /* ignore */ }
      });
      console.log(`[PusherService] Vehicle cache updated: ${id} (${mapped.vehicle_code})`);
    } catch (e) {
      console.error('[PusherService] handleVehicleUpdate error:', e);
    }
  }

  // ===== ブラウザ通知 =====

  /** タブが非アクティブならブラウザ通知を送信 */
  sendBrowserNotification(title: string, body: string): void {
    if (typeof Notification === 'undefined') return;
    if (Notification.permission !== 'granted') return;
    // タブがアクティブなら通知しない（アプリ内トーストで十分）
    if (document.visibilityState === 'visible') return;
    try {
      new Notification(title, {
        body,
        icon: '/favicon.ico',
        tag: 'dispatch-update',
      });
    } catch (e) {
      console.error('[PusherService] Notification error:', e);
    }
  }

  disconnect(): void {
    if (this.pusher) {
      this.pusher.unsubscribe('dispatch-updates');
      this.pusher.unsubscribe('line-chat');
      this.pusher.disconnect();
      this.pusher = null;
      this.channel = null;
      this.lineChannel = null;
      this.connected = false;
      console.log('[PusherService] Disconnected');
    }
  }

  /** イベントリスナーを追加 (dispatch-updates) */
  subscribe(callback: PusherEventCallback): () => void {
    this.listeners.push(callback);
    return () => {
      this.listeners = this.listeners.filter(cb => cb !== callback);
    };
  }

  /** LINE Chat イベントリスナーを追加 */
  subscribeLine(callback: PusherEventCallback): () => void {
    this.lineListeners.push(callback);
    return () => {
      this.lineListeners = this.lineListeners.filter(cb => cb !== callback);
    };
  }

  notifyListeners(eventName: string, data: any): void {
    for (const cb of this.listeners) {
      try { cb(eventName, data); } catch (e) { console.error('[PusherService] Listener error:', e); }
    }
  }

  notifyLineListeners(eventName: string, data: any): void {
    for (const cb of this.lineListeners) {
      try { cb(eventName, data); } catch (e) { console.error('[PusherService] LINE listener error:', e); }
    }
  }

  getState(): PusherState {
    return {
      connected: this.connected,
      socketId: this.pusher?.connection?.socket_id ?? null,
      state: this.pusher?.connection?.state ?? 'not_initialized',
    };
  }
}

// シングルトン
export const pusherService = new PusherService();

if (typeof window !== 'undefined') {
  (window as any).__pusherService = pusherService;
}
